import { v4 } from 'uuid';
import { t } from 'i18next';
import { Result, nextSortOrder } from '@strength/utils';
import { Scheme, SetGroupedData, BlockPreview } from './types';
import {
  WorkoutSection,
  WorkoutExercise,
  Workout,
  getMetricSpec,
  HasWorkoutLayer,
  WorkoutSet,
  ExerciseMutationData,
  SetFullMutationData,
} from '@strength/domain-workout';
import { Metric, WorkoutLayerDomain } from '@strength/domain';
import { RouterInput } from '../../../trpc';
import {
  WorkoutLayerCommand,
  WorkoutLayerModelName,
} from './mutation-fns/types';
import { WorkoutWithCycleAndSession } from './types';
import {
  addExerciseEdits,
  addSectionEdits,
  addSetEdits,
  createWorkoutLayerEdit,
  findWorkoutLayer,
  swapExerciseMetricsEdits,
} from './mutation-fns/workout-draft';
import { CreateSchemePayload } from './new-scheme-dialog';
import { FocusOption } from '../library/schemes/types';
import { WithWorkoutId } from './context-menu/data-for-context-menu';

export function selectSchemeCommands(
  block: BlockPreview,
  workouts: WorkoutWithCycleAndSession[],
  workoutLayers: HasWorkoutLayer[],
  section: WorkoutSection,
  trainingExercise: WorkoutExercise,
  scheme: Scheme,
  modelName: WorkoutLayerModelName,
  setsMetric: Metric,
  baseWorkoutLayers?: HasWorkoutLayer[],
): Result<WorkoutLayerCommand[], string> {
  const commands: WorkoutLayerCommand[] = [];

  const commonExerciseData: Omit<ExerciseMutationData, 'sortOrder'> = {
    libraryExerciseId: trainingExercise.libraryExerciseId,
    exerciseName: trainingExercise.exerciseName,
    instruction: null,
    isEachSide: trainingExercise.isEachSide ?? false,
  };

  // Match up scheme sets with workouts
  const workoutsByIndexResult = workoutsByCycleAndSessionIndex(
    block,
    workouts,
    section.workoutId,
  );
  if (!workoutsByIndexResult.ok) {
    return workoutsByIndexResult;
  }
  const workoutsByIndex = workoutsByIndexResult.value;

  const groupedSets = new Map<
    string,
    { sets: Scheme['sets']; cycleIndex: number; sessionIndex: number }
  >();
  for (const set of scheme.sets) {
    const key = `${set.cycleIndex}-${set.sessionIndex}`;
    const existing = groupedSets.get(key);
    if (existing) {
      existing.sets.push(set);
    } else {
      groupedSets.set(key, {
        sets: [set],
        cycleIndex: set.cycleIndex,
        sessionIndex: set.sessionIndex,
      });
    }
  }

  for (const { sets, cycleIndex, sessionIndex } of groupedSets.values()) {
    const matchingWorkout = workoutsByIndex[cycleIndex]?.[sessionIndex];
    if (!matchingWorkout) {
      continue;
    }
    const { workoutLayer, identity, baseLayer } = findWorkoutLayer(
      workoutLayers,
      matchingWorkout.workoutId,
      modelName,
      baseWorkoutLayers,
    );

    const edits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];

    const commonSetsData: SetFullMutationData[] = [];
    for (const set of sets) {
      const metrics: SetFullMutationData['metrics'] = [];

      for (const setMetric of set.metrics) {
        const exerciseMetric = scheme.metrics.find(
          (m) => m.metricId === setMetric.metricId,
        );
        if (!exerciseMetric) {
          return {
            ok: false,
            error: t('scheme.metricValueNotFound'),
          };
        }
        metrics.push({
          metricId: setMetric.metricId,
          input: setMetric.input,
          forCollection: setMetric.forCollection,
        });
      }

      commonSetsData.push({
        sortOrder: set.sortOrder,
        metrics,
      });
    }

    // Add to existing section if one exists with the same name, otherwise create a new one
    const matchingSection = matchingWorkout.sections.find(
      (s) => s.name === section.name,
    );
    const workoutSectionId = v4();
    if (!matchingSection) {
      edits.push(
        ...addSectionEdits(
          {
            name: section.name,
            sortOrder: nextSortOrder(matchingWorkout.sections),
            instruction: section.instruction ?? null,
            color: section.color ?? null,
          },
          workoutSectionId,
        ),
        // Schemes can only be applied to WorkoutExercises (no Supersets or AlternateGroups)
        ...addExerciseEdits(
          setsMetric,
          workoutSectionId,
          {
            ...commonExerciseData,
            sortOrder: nextSortOrder(matchingWorkout.sections),
          },
          scheme.metrics.map((m) => ({
            sortOrder: m.sortOrder,
            metricId: m.metricId,
            metricName: m.metricName,
            metricType: m.metricType,
          })),
          commonSetsData,
        ),
      );
    } else {
      const matchingExercise = matchingSection.entries.find(
        (e): e is WorkoutExercise =>
          e.__type === 'WorkoutExercise' &&
          e.libraryExerciseId === trainingExercise.libraryExerciseId,
      );
      if (!matchingExercise) {
        edits.push(
          ...addExerciseEdits(
            setsMetric,
            matchingSection.workoutSectionId,
            {
              ...commonExerciseData,
              sortOrder: nextSortOrder(matchingSection.entries),
            },
            scheme.metrics.map((m) => ({
              sortOrder: m.sortOrder,
              metricId: m.metricId,
              metricName: m.metricName,
              metricType: m.metricType,
            })),
            commonSetsData,
          ),
        );
      } else if (matchingExercise.sets.length > 0) {
        return {
          ok: false,
          error: t('scheme.exerciseHasSets'),
        };
      } else {
        edits.push(
          ...swapExerciseMetricsEdits(
            workoutLayer,
            baseLayer,
            setsMetric,
            matchingExercise.workoutExerciseId,
            scheme.metrics.map((m) => ({
              sortOrder: m.sortOrder,
              metricId: m.metricId,
              metricName: m.metricName,
              metricType: m.metricType,
            })),
          ),
          ...commonSetsData.flatMap((set) =>
            addSetEdits(
              setsMetric,
              matchingExercise.workoutExerciseId,
              { sortOrder: set.sortOrder },
              set.metrics.map((m) => ({
                metricId: m.metricId,
                input: m.input,
                forCollection: m.forCollection,
              })),
            ),
          ),
        );
      }
    }

    commands.push(createWorkoutLayerEdit(identity, workoutLayer, edits));
  }

  return {
    ok: true,
    value: commands,
  };
}

export function workoutsByCycleAndSessionIndex(
  block: BlockPreview,
  workouts: WorkoutWithCycleAndSession[],
  baseWorkoutId?: string,
): Result<(Workout | undefined)[][], string> {
  const baseWorkout = workouts.find((w) => w.workoutId === baseWorkoutId);
  // A SUPPLIED baseWorkoutId that no workout carries must not fall back to the
  // first cycle — that would silently redirect the scheme (SC-5542 family).
  if (baseWorkoutId !== undefined && !baseWorkout) {
    return { ok: false, error: t('scheme.applyFailed') };
  }

  const baseWorkoutCycleIndex = baseWorkout
    ? block.sortedCycles.findIndex((c) => c.id === baseWorkout.cycle.id)
    : 0;
  // Guard (SC-5542): never slice with a -1 index — it wraps to the last
  // entry and silently redirects the scheme to the wrong workout.
  if (baseWorkoutCycleIndex === -1) {
    return { ok: false, error: t('scheme.applyFailed') };
  }

  // SC-5542: build each cycle's row from the cycle's OWN session list
  // (block.layout) instead of the Week-1 stencil (block.sortedSessions),
  // so a cycle that grew beyond Week 1's shape still resolves. Session ids
  // are 1-based sort positions shared across cycles, so the base session's
  // position within a cycle is its id. Positions a shorter cycle doesn't
  // have are skipped — never wrapped.
  const baseSessionPosition = baseWorkout ? baseWorkout.session.id : 1;
  if (baseWorkout) {
    const baseCycleSessionCount = block.layout[baseWorkout.cycle.id] ?? 0;
    if (
      baseSessionPosition < 1 ||
      baseSessionPosition > baseCycleSessionCount
    ) {
      return { ok: false, error: t('scheme.applyFailed') };
    }
  }

  const cyclesFromBase = block.sortedCycles.slice(baseWorkoutCycleIndex);

  return {
    ok: true,
    value: cyclesFromBase.map((cycle) => {
      const sessionCount = block.layout[cycle.id] ?? 0;
      const sessionPositions: number[] = [];
      for (
        let position = baseSessionPosition;
        position <= sessionCount;
        position++
      ) {
        sessionPositions.push(position);
      }
      return sessionPositions.map((sessionId) =>
        workouts.find(
          (w) => w.cycle.id === cycle.id && w.session.id === sessionId,
        ),
      );
    }),
  };
}

export function createSchemeInput(
  block: BlockPreview,
  workouts: WorkoutWithCycleAndSession[],
  focusOptions: FocusOption[],
  schemePayload: CreateSchemePayload,
  getMetric: (id: string) => Metric,
): Result<RouterInput['schemeLibrary']['createScheme'], string> {
  const workoutsByIndexResult = workoutsByCycleAndSessionIndex(block, workouts);
  if (!workoutsByIndexResult.ok) {
    return workoutsByIndexResult;
  }
  const workoutsByIndex = workoutsByIndexResult.value;
  const workoutsByIndexMap = new Map<
    string,
    { cycleIndex: number; sessionIndex: number }
  >();
  for (const [cycleIndex, cycle] of workoutsByIndex.entries()) {
    for (const [sessionIndex, workout] of cycle.entries()) {
      if (workout) {
        workoutsByIndexMap.set(workout.workoutId, { cycleIndex, sessionIndex });
      }
    }
  }

  const { setsData, name, type, focusIds } = schemePayload;

  const first = setsData[0];
  if (!first) {
    return {
      ok: false,
      error: t('scheme.noSets'),
    };
  }
  const exercisesData = groupSetsIntoExercises(setsData);

  const selectedFocuses = focusOptions
    .filter((f) => focusIds.includes(f.id))
    .map((f, index) => {
      return { id: v4(), focusId: f.id, sortOrder: index };
    });

  const errors = new Set<string>();
  const exerciseMetrics = first.data.exercise.metrics;
  const exerciseMetricIds = exerciseMetrics
    .map((m) => m.metricId)
    .sort()
    .join(',');
  const setsInput: RouterInput['schemeLibrary']['createScheme']['sets'] = [];

  for (const {
    workoutId,
    data: { sets, exercise },
  } of exercisesData) {
    const workoutIndex = workoutsByIndexMap.get(workoutId);
    if (!workoutIndex) {
      errors.add(t('scheme.workoutNotFound'));
      continue;
    }

    const thisExerciseMetricIds = exercise.metrics
      .map((m) => m.metricId)
      .sort()
      .join(',');
    if (exerciseMetricIds !== thisExerciseMetricIds) {
      errors.add(t('scheme.setMetricsMismatch'));
      continue;
    }

    const { cycleIndex, sessionIndex } = workoutIndex;
    for (const [sortOrder, set] of sets.entries()) {
      const setMetricsInput: RouterInput['schemeLibrary']['createScheme']['sets'][0]['metrics'] =
        [];
      for (const exerciseMetric of exerciseMetrics) {
        const setMetric = set.metrics.find(
          (m) => m.metricId === exerciseMetric.metricId,
        );
        if (!setMetric) {
          errors.add(t('scheme.setsMissingMetrics'));
          continue;
        }

        const spec = getMetricSpec(getMetric(exerciseMetric.metricId));
        const valueResult = spec.schema.safeParse(setMetric.input);
        if (!valueResult.success) {
          errors.add(t('scheme.setsInvalidValues'));
          continue;
        }

        setMetricsInput.push({
          id: v4(),
          metricId: setMetric.metricId,
          input: setMetric.input,
          forCollection: setMetric.forCollection,
        });
      }

      setsInput.push({
        id: v4(),
        cycleIndex,
        sessionIndex,
        sortOrder,
        metrics: setMetricsInput,
      });
    }
  }

  if (errors.size > 0) {
    return {
      ok: false,
      error: Array.from(errors).join(', '),
    };
  }

  return {
    ok: true,
    value: {
      id: v4(),
      trainingTypeId: type.id,
      name,
      metrics: exerciseMetrics.map((m) => ({
        id: v4(),
        metricId: m.metricId,
        metricName: m.metricName,
        metricType: m.metricType,
        sortOrder: m.sortOrder,
      })),
      focuses: selectedFocuses,
      sets: setsInput,
    },
  };
}

function groupSetsIntoExercises(
  setsData: WithWorkoutId<SetGroupedData>[],
): WithWorkoutId<{
  sets: WorkoutSet[];
  exercise: WorkoutExercise;
}>[] {
  const exercisesData = new Map<
    string,
    WithWorkoutId<{
      sets: WorkoutSet[];
      exercise: WorkoutExercise;
    }>
  >();
  for (const { workoutId, data } of setsData) {
    const existing = exercisesData.get(data.exercise.workoutExerciseId);
    if (existing) {
      existing.data.sets.push(data.set);
    } else {
      exercisesData.set(data.exercise.workoutExerciseId, {
        workoutId,
        data: {
          exercise: data.exercise,
          sets: [data.set],
        },
      });
    }
  }
  return Array.from(exercisesData.values());
}
